import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  TextField,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Pause as SuspendIcon,
  HealthAndSafety as HealthIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  RocketLaunch as ProvisionIcon,
  Stop as StopIcon,
  PlayCircle as StartIcon,
  Folder as FolderIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon,
  Web as WebIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Verified as VerifiedIcon,
  ThumbUp as ApproveIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Cancel as CancelIcon,
  LockReset as LockResetIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import TenantDestroyPlanPanel from '../../components/TenantDestroyPlanPanel';
import TenantDestroyReportDialog from '../../components/TenantDestroyReportDialog';
import TenantProvisionWizard from '../../components/TenantProvisionWizard';
import { openTenantServiceUrl, withLocalIngressPort } from '../../utils/tenantUrls';
import { generateStrongPassword, ADMIN_PASSWORD_LENGTH } from '../../utils/password';

const TenantDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [infrastructureStatus, setInfrastructureStatus] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [sendForApprovalDialog, setSendForApprovalDialog] = useState(false);
  const [managers, setManagers] = useState([]);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [clientBehalfDialog, setClientBehalfDialog] = useState(false);
  const [clientBehalfReason, setClientBehalfReason] = useState('');
  const [clientBehalfLoading, setClientBehalfLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [destroyPreview, setDestroyPreview] = useState(null);
  const [destroyPreviewLoading, setDestroyPreviewLoading] = useState(false);
  const [deleteBackup, setDeleteBackup] = useState(true);
  const [deleteDestroyInfra, setDeleteDestroyInfra] = useState(true);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [destroyReport, setDestroyReport] = useState(null);
  const [destroyReportOpen, setDestroyReportOpen] = useState(false);
  const [provisionReadiness, setProvisionReadiness] = useState(null);
  const [provisionConfirmOpen, setProvisionConfirmOpen] = useState(false);
  const [provisionPanelOpen, setProvisionPanelOpen] = useState(false);
  const [revealedAdmins, setRevealedAdmins] = useState(null);
  const [revealDialogOpen, setRevealDialogOpen] = useState(false);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [resetAdminOpen, setResetAdminOpen] = useState(false);
  const [editAdminOpen, setEditAdminOpen] = useState(false);
  const [viewAdminOpen, setViewAdminOpen] = useState(false);
  const [deleteAdminOpen, setDeleteAdminOpen] = useState(false);
  const [adminActionLoading, setAdminActionLoading] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState({
    email: '',
    name: 'Admin User',
    admin_password: '',
    new_email: '',
    password: ''
  });
  const [pushedAdminResult, setPushedAdminResult] = useState(null);

  useEffect(() => {
    fetchTenant();
  }, [id]);

  useEffect(() => {
    let intervalId;
    if (tenant?.provisioning_status === 'in_progress') {
      intervalId = setInterval(() => {
        fetchProgress(false);
        fetchTenant(false);
      }, 2000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [tenant?.provisioning_status]);

  useEffect(() => {
    if (tenant?.provisioning_status === 'completed') {
      fetchInfrastructureStatus();
      fetchDeploymentProgress();
    }
  }, [tenant?.provisioning_status]);

  useEffect(() => {
    if (tenant?.status === 'approved' && tenant?.provisioning_status === 'ready') {
      fetchProvisionReadiness();
    }
  }, [tenant?.status, tenant?.provisioning_status]);

  useEffect(() => {
    if (searchParams.get('provision') === 'start' && tenant?.provisioning_status === 'ready') {
      setProvisionConfirmOpen(true);
      searchParams.delete('provision');
      setSearchParams(searchParams, { replace: true });
    }
  }, [tenant?.provisioning_status, searchParams, setSearchParams]);

  const fetchTenant = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await api.get(`/tenants/${id}`);
      setTenant(response.data.data);
    } catch (err) {
      toast.error('Failed to load tenant');
      navigate('/tenants');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await api.get(`/provisioning/${id}/progress`);
      setProgressData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch progress:', err);
    }
  };

  const fetchProvisionReadiness = async () => {
    try {
      const response = await api.get(`/provisioning/${id}/readiness`);
      setProvisionReadiness(response.data.data);
    } catch (err) {
      console.error('Failed to fetch provision readiness:', err);
      setProvisionReadiness(null);
    }
  };

  const fetchInfrastructureStatus = async () => {
    try {
      const response = await api.get(`/provisioning/${id}/status`);
      setInfrastructureStatus(response.data.data.infrastructure);
    } catch (err) {
      console.error('Failed to fetch infrastructure status');
    }
  };

  const fetchDeploymentProgress = async () => {
    try {
      const response = await api.get(`/provisioning/${id}/deployment/progress`);
      setDeploymentProgress(response.data.data);
    } catch (err) {
      console.error('Failed to fetch deployment progress');
    }
  };

  // Workflow Actions
  const handleVerify = async () => {
    try {
      await api.post(`/tenants/${id}/verify`);
      toast.success('Tenant verified');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify');
    }
  };

  const handleApproveOnBehalfOfClient = async () => {
    setClientBehalfLoading(true);
    try {
      await api.post(`/tenants/${id}/approve-on-behalf-of-client`, {
        reason: clientBehalfReason.trim() || undefined
      });
      toast.success('Approved on behalf of client. You can now verify.');
      setClientBehalfDialog(false);
      setClientBehalfReason('');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve on behalf of client');
    } finally {
      setClientBehalfLoading(false);
    }
  };

  const openDeleteDialog = async () => {
    setDestroyPreview(null);
    setDeleteConfirmName('');
    setDeleteDestroyInfra(true);
    setDeleteBackup(true);
    setDeleteDialog(true);
    if (user?.role !== 'super_admin') return;
    setDestroyPreviewLoading(true);
    try {
      const response = await api.get(`/tenants/${id}/destroy-preview`);
      const preview = response.data?.data;
      setDestroyPreview(preview);
      setDeleteBackup(Boolean(preview?.backup_recommended));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load destroy preview');
    } finally {
      setDestroyPreviewLoading(false);
    }
  };

  const handleForceDelete = async () => {
    if (deleteConfirmName.trim() !== tenant.tenant_name) {
      toast.error('Type the tenant name exactly to confirm destroy');
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await api.delete(`/tenants/${id}`, {
        params: {
          force: true,
          backup: deleteBackup,
          destroy: deleteDestroyInfra
        }
      });
      const data = response.data?.data || {};
      setDeleteDialog(false);
      toast.success(response.data?.message || 'Delete/Destroy started');
      await fetchTenant(false);
      if (data.status === 'destroying' || data.mode === 'async_destroy') {
        setDestroyReport({
          mode: 'async_destroy',
          status: 'destroying',
          destroy_progress: data.destroy_progress,
          message: 'Click status on Tenants list to watch live progress'
        });
        setDestroyReportOpen(true);
      } else {
        setDestroyReport(data);
        setDestroyReportOpen(true);
      }
    } catch (err) {
      const body = err.response?.data || {};
      if (body.data?.infrastructure || body.data?.steps) {
        setDestroyReport(body.data);
        setDestroyReportOpen(true);
      }
      toast.error(body.message || 'Failed to destroy tenant');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openVerificationDetails = async () => {
    try {
      const response = await api.get(`/tenants/${id}/verification-details`);
      setVerificationDetails(response.data.data);
      setVerifyDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load verification details');
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/tenants/${id}/approve`);
      toast.success('Tenant approved');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    try {
      await api.post(`/tenants/${id}/reject`, { reason: rejectReason });
      toast.success('Tenant rejected');
      setRejectDialog(false);
      setRejectReason('');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    }
  };

  const openSendForApprovalDialog = async () => {
    try {
      const response = await api.get('/tenants/managers');
      setManagers(response.data.data || []);
      setSelectedManagerId('');
      setSendForApprovalDialog(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load managers');
    }
  };

  const handleSendForApproval = async () => {
    try {
      await api.post(`/tenants/${id}/send-for-approval`, { manager_id: selectedManagerId || undefined });
      toast.success('Tenant sent for approval. A Manager or Super Admin can now approve.');
      setSendForApprovalDialog(false);
      setSelectedManagerId('');
      fetchTenant();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send for approval');
    }
  };

  const handleStartProvisioning = () => {
    setProvisionConfirmOpen(true);
  };

  const handleActivate = async () => {
    try {
      await api.post(`/tenants/${id}/activate`);
      toast.success('Tenant activated');
      fetchTenant();
    } catch (err) {
      toast.error('Failed to activate');
    }
  };

  const handleSuspend = async () => {
    try {
      await api.post(`/tenants/${id}/suspend`, { reason: suspendReason });
      toast.success('Tenant suspended');
      setSuspendDialog(false);
      setSuspendReason('');
      fetchTenant();
    } catch (err) {
      toast.error('Failed to suspend');
    }
  };

  const handleRevealCredentials = async () => {
    if (!revealPassword) return;
    setRevealLoading(true);
    try {
      const res = await api.post(`/tenants/${id}/reveal-admin-credentials`, {
        password: revealPassword
      });
      const admins = res.data?.data?.admins || [];
      setRevealedAdmins(admins);
      setRevealDialogOpen(false);
      setRevealPassword('');
      toast.success('Credentials revealed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Incorrect password or reveal failed');
    } finally {
      setRevealLoading(false);
    }
  };

  const emptyAdminForm = () => ({
    email: '',
    name: 'Admin User',
    admin_password: '',
    new_email: '',
    password: ''
  });

  const listTenantAdmins = () => {
    if (Array.isArray(tenant?.admin_users) && tenant.admin_users.length) {
      return tenant.admin_users;
    }
    if (tenant?.admin_email) {
      return [{ email: tenant.admin_email, name: '', role: 'admin', has_password: tenant.has_admin_password }];
    }
    return [];
  };

  const mergeRevealedPassword = (admin) => {
    const revealed = (revealedAdmins || []).find(
      (a) => String(a.email || '').toLowerCase() === String(admin?.email || '').toLowerCase()
    );
    return {
      ...admin,
      password: revealed?.password || admin?.password || null
    };
  };

  const applyAdminResult = (data, fallbackName) => {
    if (!data?.email) return;
    setPushedAdminResult(data);
    const next = {
      email: data.email,
      name: data.name || fallbackName || 'Admin User',
      role: 'admin',
      password: data.password
    };
    setRevealedAdmins((prev) => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex((a) => String(a.email || '').toLowerCase() === String(data.email).toLowerCase());
      if (idx >= 0) list[idx] = { ...list[idx], ...next };
      else list.push(next);
      return list;
    });
  };

  const handleAddAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error('Admin email and your Super Admin password are required');
      return;
    }
    setAdminActionLoading(true);
    try {
      const res = await api.post(`/tenants/${id}/push-admin-user`, {
        mode: 'add',
        email: adminForm.email.trim(),
        name: adminForm.name.trim() || 'Admin User',
        admin_password: adminForm.admin_password || undefined,
        password: adminForm.password
      });
      const data = res.data?.data || {};
      applyAdminResult(data, adminForm.name);
      setAddAdminOpen(false);
      setAdminForm(emptyAdminForm());
      toast.success(res.data?.message || 'Admin added');
      fetchTenant(false);
    } catch (err) {
      const body = err.response?.data || {};
      if (body.data?.password || body.password) applyAdminResult(body.data || body, adminForm.name);
      toast.error(body.message || 'Failed to add admin');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleResetAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error('Select a user and enter your Super Admin password');
      return;
    }
    setAdminActionLoading(true);
    try {
      const res = await api.post(`/tenants/${id}/push-admin-user`, {
        mode: 'reset',
        email: adminForm.email.trim(),
        password: adminForm.password
      });
      const data = res.data?.data || {};
      applyAdminResult(data);
      setResetAdminOpen(false);
      setAdminForm(emptyAdminForm());
      toast.success(res.data?.message || 'Password reset');
      fetchTenant(false);
    } catch (err) {
      const body = err.response?.data || {};
      if (body.data?.password || body.password) applyAdminResult(body.data || body);
      toast.error(body.message || 'Failed to reset password');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleEditAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error('Your Super Admin password is required');
      return;
    }
    setAdminActionLoading(true);
    try {
      const res = await api.post(`/tenants/${id}/push-admin-user`, {
        mode: 'update',
        email: adminForm.email.trim(),
        new_email: adminForm.new_email?.trim() || undefined,
        name: adminForm.name.trim() || 'Admin User',
        admin_password: adminForm.admin_password || undefined,
        password: adminForm.password
      });
      const data = res.data?.data || {};
      applyAdminResult(data, adminForm.name);
      setEditAdminOpen(false);
      setSelectedAdmin(null);
      setAdminForm(emptyAdminForm());
      toast.success(res.data?.message || 'Admin updated');
      fetchTenant(false);
    } catch (err) {
      const body = err.response?.data || {};
      if (body.data?.password || body.password) applyAdminResult(body.data || body, adminForm.name);
      toast.error(body.message || 'Failed to update admin');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!adminForm.email || !adminForm.password) {
      toast.error('Your Super Admin password is required');
      return;
    }
    const emailToRemove = adminForm.email.trim().toLowerCase();
    setAdminActionLoading(true);
    try {
      await api.post(`/tenants/${id}/remove-admin-user`, {
        email: adminForm.email.trim(),
        password: adminForm.password
      });
      setDeleteAdminOpen(false);
      setSelectedAdmin(null);
      setAdminForm(emptyAdminForm());
      setRevealedAdmins((prev) => (Array.isArray(prev)
        ? prev.filter((a) => String(a.email || '').toLowerCase() !== emailToRemove)
        : prev));
      toast.success('Admin removed');
      fetchTenant(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove admin');
    } finally {
      setAdminActionLoading(false);
    }
  };

  const handleStartContainers = async () => {
    try {
      await api.post(`/provisioning/${id}/containers/start`);
      toast.success('Containers started');
      fetchInfrastructureStatus();
    } catch (err) {
      toast.error('Failed to start containers');
    }
  };

  const handleStopContainers = async () => {
    try {
      await api.post(`/provisioning/${id}/containers/stop`);
      toast.success('Containers stopped');
      fetchInfrastructureStatus();
    } catch (err) {
      toast.error('Failed to stop containers');
    }
  };

  const handleHealthCheck = async () => {
    try {
      const response = await api.post(`/tenants/${id}/health-check`);
      toast.success(`Health: ${response.data.data.health_status}`);
      fetchTenant();
    } catch (err) {
      toast.error('Health check failed');
    }
  };

  const runDeploymentAction = async (action) => {
    setDeploymentLoading(true);
    try {
      await api.post(`/provisioning/${id}/deployment/run`, { action });
      toast.success(`Deployment action completed: ${action}`);
      fetchDeploymentProgress();
      fetchTenant(false);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed: ${action}`);
      fetchDeploymentProgress();
    } finally {
      setDeploymentLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'password') {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    toast.success(`${type === 'password' ? 'Password' : 'Tenant ID'} copied`);
  };

  const getStatusChip = (status) => {
    const isActive = status === 'active';
    return (
      <Chip
        label={isActive ? 'Active' : (status === 'suspended' ? 'Suspended' : String(status || 'inactive').replace(/_/g, ' '))}
        size="small"
        icon={isActive ? <CheckIcon /> : <CancelIcon />}
        sx={{
          fontWeight: 700,
          textTransform: 'capitalize',
          color: '#fff',
          bgcolor: isActive ? '#2e7d32' : '#c62828',
          '& .MuiChip-icon': { color: '#fff' }
        }}
      />
    );
  };

  const getProvisioningChip = (status) => {
    const config = {
      not_started: { color: 'default', label: 'Not Started' },
      ready: { color: 'info', label: 'Ready to Start' },
      in_progress: { color: 'warning', label: 'In Progress' },
      completed: { color: 'success', label: 'Completed' },
      failed: { color: 'error', label: 'Failed' }
    };
    const c = config[status] || config.not_started;
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const currentSteps = progressData?.steps || tenant?.provisioning_steps || [];
  const progressPercent = currentSteps.length > 0 
    ? Math.round((currentSteps.filter(s => s.status === 'completed' || s.status === 'warning').length / currentSteps.length) * 100)
    : 0;

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  if (!tenant) {
    return <Alert severity="error">Tenant not found</Alert>;
  }

  const canRunFinalActions = user?.role === 'manager' || user?.role === 'super_admin';

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/tenants')}>Back</Button>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{tenant.tenant_name}</Typography>
          <Typography variant="body2" color="text.secondary">{tenant.company_name}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/tenants/${id}/edit`)}>
          Edit
        </Button>
        {user?.role === 'super_admin' && tenant.provisioning_status !== 'in_progress' && (
          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={openDeleteDialog}>
            Delete & Destroy
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Tenant ID Card */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white' }}>
            <Typography variant="overline" sx={{ opacity: 0.7 }}>TENANT ID</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Typography variant="h3" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 3 }}>
                {tenant.tenant_id}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                <IconButton onClick={() => copyToClipboard(tenant.tenant_id, 'id')} sx={{ color: 'white' }}>
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                {getStatusChip(tenant.status)}
                {getProvisioningChip(tenant.provisioning_status)}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Workflow Actions Card */}
        <Grid item xs={12}>
          <Card sx={{ border: '2px solid', borderColor: 'primary.light' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Workflow Actions</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {user?.role === 'super_admin' && ['submitted', 'client_approved', 'erp_verified', 'approved', 'in_progress'].includes(tenant.status) && (
                  <Alert severity="info" sx={{ width: '100%', mb: 1 }}>
                    As Super Admin you can run the full tenant process alone (client approval, verify, final approve, provision, activate) to validate the flow. Overrides are recorded in the activity log.
                  </Alert>
                )}
                {tenant.status === 'submitted' && user?.role === 'super_admin' && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<ApproveIcon />}
                    onClick={() => setClientBehalfDialog(true)}
                  >
                    Approve on Behalf of Client
                  </Button>
                )}
                {tenant.status === 'client_approved' && (
                  <Button variant="contained" color="info" startIcon={<VerifiedIcon />} onClick={openVerificationDetails}>
                    {user?.role === 'super_admin' ? 'Verify (Super Admin OK)' : 'Verify Details'}
                  </Button>
                )}
                {['submitted', 'client_approved', 'erp_verified', 'approved', 'in_progress'].includes(tenant.status) && (
                  <Button variant="outlined" onClick={openVerificationDetails}>
                    View Approval Details
                  </Button>
                )}
                {tenant.status === 'erp_verified' && (
                  <>
                    <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                      {user?.role === 'super_admin'
                        ? 'You can approve even if you verified — useful for end-to-end process checks.'
                        : 'Final approval must be done by a Manager or Super Admin (different from the person who verified). If you are Admin, use "Send for approval" to notify a manager.'}
                    </Alert>
                    <Button variant="contained" color="success" startIcon={<ApproveIcon />} onClick={handleApprove}>
                      {user?.role === 'super_admin' ? 'Approve (Super Admin)' : 'Approve (Manager / Super Admin)'}
                    </Button>
                    <Button variant="outlined" startIcon={<InfoIcon />} onClick={openSendForApprovalDialog}>
                      Send for approval
                    </Button>
                    <Button variant="outlined" color="error" onClick={() => setRejectDialog(true)}>
                      Reject
                    </Button>
                  </>
                )}
                {tenant.status === 'approved' && tenant.provisioning_status === 'ready' && (
                  <Box sx={{ width: '100%' }}>
                    {provisionReadiness && (
                      <Alert
                        severity={provisionReadiness.ok ? 'success' : 'warning'}
                        sx={{ mb: 2 }}
                        action={(
                          <Button color="inherit" size="small" onClick={fetchProvisionReadiness}>
                            Refresh
                          </Button>
                        )}
                      >
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Provision readiness ({provisionReadiness.mode})
                        </Typography>
                        {provisionReadiness.ok
                          ? 'Ready — click Start Provisioning to review the full step plan, then confirm.'
                          : `Blocked: ${(provisionReadiness.errors || []).join('; ')}`}
                        {(provisionReadiness.warnings || []).length > 0 && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                            Warnings: {provisionReadiness.warnings.join('; ')}
                          </Typography>
                        )}
                      </Alert>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ProvisionIcon />}
                      onClick={handleStartProvisioning}
                      size="large"
                      disabled={provisionReadiness && !provisionReadiness.ok}
                    >
                      Start Provisioning
                    </Button>
                  </Box>
                )}
                {(tenant.provisioning_status === 'in_progress'
                  || tenant.provisioning_status === 'completed'
                  || tenant.provisioning_status === 'failed') && (
                  <Button variant="outlined" startIcon={<ProvisionIcon />} onClick={() => setProvisionPanelOpen(true)}>
                    Open provision progress
                  </Button>
                )}
                {tenant.provisioning_status === 'completed' && tenant.status !== 'active' && (
                  <Button variant="contained" color="success" startIcon={<ActivateIcon />} onClick={handleActivate}>
                    Activate Tenant
                  </Button>
                )}
                {tenant.status === 'active' && (
                  <Button variant="outlined" color="warning" startIcon={<SuspendIcon />} onClick={() => setSuspendDialog(true)}>
                    Suspend
                  </Button>
                )}
                {tenant.provisioning_status === 'completed' && (
                  <Button variant="outlined" startIcon={<HealthIcon />} onClick={handleHealthCheck}>
                    Health Check
                  </Button>
                )}
              </Box>
              {tenant.rejection_reason && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>Rejection Reason:</strong> {tenant.rejection_reason}
                </Alert>
              )}
              {!canRunFinalActions && tenant.provisioning_status === 'completed' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Final deployment actions (build/apply/standup) are restricted to manager/super_admin.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Provisioning Progress — live details live in the right-hand panel */}
        {tenant.provisioning_status === 'in_progress' && (
          <Grid item xs={12}>
            <Card sx={{ border: '2px solid', borderColor: 'info.main' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <CircularProgress size={24} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Provisioning in progress</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {progressPercent}% complete — open the right-hand panel for step-by-step status
                    </Typography>
                  </Box>
                  <Button variant="contained" onClick={() => setProvisionPanelOpen(true)}>
                    Open progress panel
                  </Button>
                </Box>
                <LinearProgress variant="determinate" value={progressPercent} sx={{ height: 8, borderRadius: 4, mt: 2 }} />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Admin Credentials */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <LockIcon color="primary" />
                <Typography variant="h6" sx={{ flex: 1 }}>Tenant Admin Credentials</Typography>
                {tenant.provisioning_status === 'completed' && (user?.role === 'manager' || user?.role === 'super_admin') && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PersonAddIcon />}
                      onClick={() => {
                        setAdminForm(emptyAdminForm());
                        setAddAdminOpen(true);
                      }}
                    >
                      Add New Admin
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LockResetIcon />}
                      onClick={() => {
                        setAdminForm(emptyAdminForm());
                        setResetAdminOpen(true);
                      }}
                    >
                      Reset Existing Admin Password
                    </Button>
                  </>
                )}
                {revealedAdmins ? (
                  <Button
                    size="small"
                    startIcon={<VisibilityOffIcon />}
                    onClick={() => { setRevealedAdmins(null); setRevealPassword(''); setPushedAdminResult(null); }}
                  >
                    Hide
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    disabled={!tenant.has_admin_password && !(tenant.admin_email)}
                    onClick={() => setRevealDialogOpen(true)}
                  >
                    Reveal
                  </Button>
                )}
              </Box>
              {!revealedAdmins && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Passwords stay hidden until you Reveal. Use Add New Admin or Reset Existing Admin Password to manage access.
                </Alert>
              )}
              {pushedAdminResult?.password && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Updated <strong>{pushedAdminResult.email}</strong>. Password shown below — copy it now.
                </Alert>
              )}
              <Table size="small">
                <TableBody>
                  {listTenantAdmins().length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        <Typography variant="body2" color="text.secondary">No admin users listed.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {listTenantAdmins().map((admin) => {
                    const row = mergeRevealedPassword(admin);
                    return (
                      <React.Fragment key={row.email || 'admin'}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, width: 120, verticalAlign: 'top' }}>User</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                                  {row.email || '-'}
                                </Typography>
                                {(row.name || row.role) && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {[row.name, row.role].filter(Boolean).join(' · ')}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1, borderRadius: 1, wordBreak: 'break-all' }}
                                  >
                                    {revealedAdmins && row.password ? row.password : '••••••••••••'}
                                  </Typography>
                                  {revealedAdmins && row.password && (
                                    <Tooltip title={copiedPassword ? 'Copied!' : 'Copy Password'}>
                                      <IconButton size="small" onClick={() => copyToClipboard(row.password, 'password')}>
                                        {copiedPassword ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                                <Tooltip title="View">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedAdmin(row);
                                      setViewAdminOpen(true);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {(user?.role === 'manager' || user?.role === 'super_admin') && tenant.provisioning_status === 'completed' && (
                                  <>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedAdmin(row);
                                          setAdminForm({
                                            email: row.email || '',
                                            name: row.name || 'Admin User',
                                            new_email: row.email || '',
                                            admin_password: '',
                                            password: ''
                                          });
                                          setEditAdminOpen(true);
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                          setSelectedAdmin(row);
                                          setAdminForm({
                                            ...emptyAdminForm(),
                                            email: row.email || ''
                                          });
                                          setDeleteAdminOpen(true);
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>


        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6">Contact Information</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>Email</TableCell>
                    <TableCell>{tenant.contact_email}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                    <TableCell>{tenant.account?.account_name || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                    <TableCell>{tenant.contact ? `${tenant.contact.first_name} ${tenant.contact.last_name || ''}` : '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell>{tenant.contact_phone || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                    <TableCell>{tenant.company_address || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Office Direct</TableCell>
                    <TableCell>{tenant.office_direct_number || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Website</TableCell>
                    <TableCell>{tenant.website || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Environment</TableCell>
                    <TableCell>{tenant.environment === 'custom' ? `custom (${tenant.custom_environment_name || ''})` : (tenant.environment || '-')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                    <TableCell>
                      <Chip label={tenant.plan} size="small" color={tenant.plan === 'enterprise' ? 'secondary' : 'primary'} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Max Users</TableCell>
                    <TableCell>{tenant.max_users}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.source_type === 'ghe_clone' ? 'GHE Clone' : 'Base Local'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Source Location</TableCell>
                    <TableCell>{tenant.source_location || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Source Version</TableCell>
                    <TableCell>{tenant.source_version || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>ACR Images</TableCell>
                    <TableCell>
                      {Array.isArray(tenant.acr_images) && tenant.acr_images.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {tenant.acr_images.map((row) => (
                            <Typography key={`${row.repository}:${row.tag}`} variant="body2">
                              {row.repository}:{row.tag}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Target Location</TableCell>
                    <TableCell>{tenant.target_location || '-'}</TableCell>
                  </TableRow>
                  {tenant.source_type === 'ghe_clone' && (
                    <>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>GHE URL</TableCell>
                        <TableCell>{tenant.ghe_repo_url || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>GHE Ref</TableCell>
                        <TableCell>{tenant.ghe_ref || 'main'}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Infrastructure Details */}
        {tenant.provisioning_status === 'completed' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6">Infrastructure Details</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button variant="outlined" color="success" startIcon={<StartIcon />} onClick={handleStartContainers} size="small">
                    Start
                  </Button>
                  <Button variant="outlined" color="error" startIcon={<StopIcon />} onClick={handleStopContainers} size="small">
                    Stop
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FolderIcon color="primary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {tenant.folder_path?.split('/').pop() || '-'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {tenant.folder_path}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="text.secondary">Database</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <DatabaseIcon color="success" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {tenant.database_name || '-'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Port: {tenant.postgres_port}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" color="text.secondary">Container Prefix</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <SettingsIcon color="secondary" />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {tenant.container_prefix || '-'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Services */}
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'blue.50', border: '1px solid', borderColor: 'primary.light' }}>
                      <Typography variant="subtitle2" color="text.secondary">Frontend</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <WebIcon color="primary" />
                        <Typography variant="body2" sx={{ flex: 1 }}>Port {tenant.frontend_port}</Typography>
                        {tenant.frontend_url && (
                          <IconButton size="small" onClick={() => openTenantServiceUrl(tenant.frontend_url)}>
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: 12 }}>
                        {withLocalIngressPort(tenant.frontend_url) || '—'}
                      </Typography>
                      {infrastructureStatus?.containers?.find(c => c.name.includes('frontend')) && (
                        <Chip label="Running" size="small" color="success" sx={{ mt: 1 }} />
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'purple.50', border: '1px solid', borderColor: 'secondary.light' }}>
                      <Typography variant="subtitle2" color="text.secondary">Backend API</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <ApiIcon color="secondary" />
                        <Typography variant="body2" sx={{ flex: 1 }}>Port {tenant.backend_port}</Typography>
                        {tenant.backend_url && (
                          <IconButton
                            size="small"
                            onClick={() => openTenantServiceUrl(`${String(tenant.backend_url).replace(/\/$/, '')}/health`)}
                          >
                            <OpenIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: 12 }}>
                        {withLocalIngressPort(tenant.backend_url) || '—'}
                      </Typography>
                      {infrastructureStatus?.containers?.find(c => c.name.includes('backend')) && (
                        <Chip label="Running" size="small" color="success" sx={{ mt: 1 }} />
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'green.50', border: '1px solid', borderColor: 'success.light' }}>
                      <Typography variant="subtitle2" color="text.secondary">Database</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <DatabaseIcon color="success" />
                        <Typography variant="body2" sx={{ flex: 1 }}>Port {tenant.postgres_port}</Typography>
                      </Box>
                      {infrastructureStatus?.containers?.find(c => c.name.includes('postgres')) && (
                        <Chip label="Running" size="small" color="success" sx={{ mt: 1 }} />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Final Deployment Actions */}
        {tenant.provisioning_status === 'completed' && (
          <Grid item xs={12}>
            <Card sx={{ border: '2px solid', borderColor: 'secondary.light' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Final Deployment Actions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Run in order: Build Image -> Apply -> Standup
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Button
                    variant="contained"
                    disabled={!canRunFinalActions || deploymentLoading}
                    onClick={() => runDeploymentAction('build_image')}
                  >
                    Build Image
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!canRunFinalActions || deploymentLoading}
                    onClick={() => runDeploymentAction('apply')}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={!canRunFinalActions || deploymentLoading}
                    onClick={() => runDeploymentAction('standup')}
                  >
                    Standup
                  </Button>
                  <Button variant="outlined" onClick={fetchDeploymentProgress}>
                    Refresh
                  </Button>
                </Box>

                {deploymentProgress && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Deployment Status: <strong>{deploymentProgress.deployment_status}</strong>
                      </Typography>
                      <LinearProgress variant="determinate" value={deploymentProgress.progress_percent || 0} />
                    </Box>
                    {(deploymentProgress.steps || []).map((step) => (
                      <Paper key={step.action} sx={{ p: 1.5, mb: 1, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2">{step.action}</Typography>
                        <Typography variant="caption" color="text.secondary">Status: {step.status}</Typography>
                        {step.error && <Alert severity="error" sx={{ mt: 1 }}>{step.error}</Alert>}
                      </Paper>
                    ))}
                    {deploymentProgress.deployment_error && (
                      <Alert severity="error" sx={{ mt: 1 }}>{deploymentProgress.deployment_error}</Alert>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Dates & Audit */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarIcon color="primary" />
                <Typography variant="h6">Timeline</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell>{new Date(tenant.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                  {tenant.verified_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Verified</TableCell>
                      <TableCell>{new Date(tenant.verified_at).toLocaleString()}</TableCell>
                    </TableRow>
                  )}
                  {tenant.client_approved_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Client Approved</TableCell>
                      <TableCell>
                        {new Date(tenant.client_approved_at).toLocaleString()}
                        {tenant.client_approved_by_email
                          ? ` · ${tenant.client_approved_by_email}`
                          : ''}
                      </TableCell>
                    </TableRow>
                  )}
                  {tenant.erp_verified_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>ERPForC Verified</TableCell>
                      <TableCell>{new Date(tenant.erp_verified_at).toLocaleString()}</TableCell>
                    </TableRow>
                  )}
                  {tenant.approved_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Approved</TableCell>
                      <TableCell>{new Date(tenant.approved_at).toLocaleString()}</TableCell>
                    </TableRow>
                  )}
                  {tenant.provisioning_started_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Provisioning Started</TableCell>
                      <TableCell>{new Date(tenant.provisioning_started_at).toLocaleString()}</TableCell>
                    </TableRow>
                  )}
                  {tenant.provisioning_completed_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Provisioning Completed</TableCell>
                      <TableCell>{new Date(tenant.provisioning_completed_at).toLocaleString()}</TableCell>
                    </TableRow>
                  )}
                  {tenant.expires_at && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                      <TableCell>{new Date(tenant.expires_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Health Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HealthIcon color="primary" />
                <Typography variant="h6">Health Status</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={tenant.health_status || 'Unknown'}
                  color={tenant.health_status === 'healthy' ? 'success' : tenant.health_status === 'unhealthy' ? 'error' : 'default'}
                  size="large"
                />
                {tenant.last_health_check && (
                  <Typography variant="body2" color="text.secondary">
                    Last checked: {new Date(tenant.last_health_check).toLocaleString()}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Logs */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HistoryIcon color="primary" />
                <Typography variant="h6">Activity Log</Typography>
              </Box>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {tenant.activityLogs?.map((log, idx) => (
                  <React.Fragment key={log.id}>
                    {idx > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon>
                        <Chip label={log.action} size="small" variant="outlined" />
                      </ListItemIcon>
                      <ListItemText
                        primary={log.description}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(log.createdAt).toLocaleString()}
                            </Typography>
                            {log.performedBy && (
                              <Typography variant="caption" color="text.secondary">
                                {' • '}{log.performedBy.first_name} {log.performedBy.last_name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
                {(!tenant.activityLogs || tenant.activityLogs.length === 0) && (
                  <ListItem><ListItemText primary="No activity recorded yet" /></ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        {tenant.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Notes</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{tenant.notes}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Send for approval Dialog */}
      <Dialog open={sendForApprovalDialog} onClose={() => setSendForApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send for approval</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {user?.role === 'super_admin'
              ? 'As Super Admin you can approve even if you verified. Optionally notify a manager below.'
              : 'Only a Manager or Super Admin can perform final approval (and must be a different person than the verifier). Optionally assign to a manager below.'}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Notify manager (optional)</InputLabel>
            <Select
              value={selectedManagerId}
              label="Notify manager (optional)"
              onChange={(e) => setSelectedManagerId(e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {managers.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.email})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendForApprovalDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendForApproval}>Send for approval</Button>
        </DialogActions>
      </Dialog>

      {/* Super Admin Delete & Destroy */}
      <Dialog
        open={deleteDialog}
        onClose={() => !deleteLoading && setDeleteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Delete & Destroy Tenant</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="error">
              Review the numbered plan below. Prefer <strong>Backup &amp; Destroy</strong> so you can restore later.
            </Alert>
            {destroyPreviewLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading destroy plan…</Typography>
              </Box>
            )}
            {destroyPreview && (
              <TenantDestroyPlanPanel preview={destroyPreview} backupEnabled={deleteBackup} />
            )}
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={deleteBackup} onChange={(e) => setDeleteBackup(e.target.checked)} color="warning" />}
                label="Step 0 — Create backup before destroy (required for rollback)"
              />
              <FormControlLabel
                control={<Checkbox checked={deleteDestroyInfra} onChange={(e) => setDeleteDestroyInfra(e.target.checked)} />}
                label="Run destroy steps 1–7 (validate, ingress/TLS, apps, seed check, DB, namespace, records)"
              />
            </FormGroup>
            <TextField
              fullWidth
              label={`Type "${tenant.tenant_name}" to confirm`}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              disabled={deleteLoading}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleForceDelete}
            disabled={
              deleteLoading
              || destroyPreviewLoading
              || deleteConfirmName.trim() !== tenant.tenant_name
            }
          >
            {deleteLoading ? 'Destroying…' : (deleteBackup ? 'Backup & Destroy' : 'Destroy Without Backup')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve on behalf of client (Super Admin) */}
      <Dialog open={clientBehalfDialog} onClose={() => !clientBehalfLoading && setClientBehalfDialog(false)}>
        <DialogTitle>Approve on Behalf of Client</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This records client approval as Super Admin because the client is unavailable or has not approved.
            The tenant will move to <strong>Client Approved</strong> so verification can continue.
          </Alert>
          <TextField
            fullWidth
            label="Reason (optional)"
            multiline
            rows={3}
            value={clientBehalfReason}
            onChange={(e) => setClientBehalfReason(e.target.value)}
            placeholder="e.g. Client unreachable; verbal approval received"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientBehalfDialog(false)} disabled={clientBehalfLoading}>Cancel</Button>
          <Button
            onClick={handleApproveOnBehalfOfClient}
            color="warning"
            variant="contained"
            disabled={clientBehalfLoading}
          >
            {clientBehalfLoading ? 'Approving…' : 'Approve on Behalf of Client'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
        <DialogTitle>Reject Tenant</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Please provide a reason for rejection.</Typography>
          <TextField
            fullWidth
            required
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectReason}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Prompt Dialog */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Verification Details</DialogTitle>
        <DialogContent>
          {verificationDetails ? (
            <Table size="small">
              <TableBody>
                <TableRow><TableCell>Tenant</TableCell><TableCell>{verificationDetails.tenant?.tenant_name} ({verificationDetails.tenant?.tenant_id})</TableCell></TableRow>
                <TableRow><TableCell>Version</TableCell><TableCell>{verificationDetails.source?.source_version || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Source Type</TableCell><TableCell>{verificationDetails.source?.source_type || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Source Location</TableCell><TableCell>{verificationDetails.source?.source_location || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Destination Location</TableCell><TableCell>{verificationDetails.destination?.target_location || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Helm Chart</TableCell><TableCell>{verificationDetails.deployment_artifacts?.helm_chart || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Terraform Path</TableCell><TableCell>{verificationDetails.deployment_artifacts?.terraform_path || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Namespace</TableCell><TableCell>{verificationDetails.deployment_artifacts?.namespace || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Database Name</TableCell><TableCell>{verificationDetails.deployment_artifacts?.database_name || '-'}</TableCell></TableRow>
                <TableRow><TableCell>Container Prefix</TableCell><TableCell>{verificationDetails.deployment_artifacts?.container_prefix || '-'}</TableCell></TableRow>
              </TableBody>
            </Table>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Close</Button>
          {tenant.status === 'client_approved' && (
            <Button
              variant="contained"
              color="info"
              onClick={async () => {
                await handleVerify();
                setVerifyDialog(false);
              }}
            >
              Verify Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
        <DialogTitle>Suspend Tenant</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Are you sure you want to suspend this tenant?</Typography>
          <TextField
            fullWidth
            label="Suspension Reason"
            multiline
            rows={3}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialog(false)}>Cancel</Button>
          <Button onClick={handleSuspend} color="warning" variant="contained">
            Suspend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal admin credentials — re-enter Super Admin password */}
      <Dialog
        open={revealDialogOpen}
        onClose={() => { if (!revealLoading) { setRevealDialogOpen(false); setRevealPassword(''); } }}
      >
        <DialogTitle>Reveal Admin Credentials</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Re-enter your Super Admin password to view the initial tenant admin user and password.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Your Super Admin password"
            autoFocus
            autoComplete="current-password"
            value={revealPassword}
            onChange={(e) => setRevealPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRevealCredentials();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            disabled={revealLoading}
            onClick={() => { setRevealDialogOpen(false); setRevealPassword(''); }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={revealLoading || !revealPassword}
            onClick={handleRevealCredentials}
          >
            {revealLoading ? <CircularProgress size={20} /> : 'Reveal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View admin */}
      <Dialog open={viewAdminOpen} onClose={() => setViewAdminOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Admin details</DialogTitle>
        <DialogContent>
          {selectedAdmin && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Typography variant="body2"><strong>Email:</strong> {selectedAdmin.email || '—'}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {selectedAdmin.name || '—'}</Typography>
              <Typography variant="body2"><strong>Role:</strong> {selectedAdmin.role || 'admin'}</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                <strong>Password:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }}>
                  {mergeRevealedPassword(selectedAdmin).password || (revealedAdmins ? '—' : 'Hidden — use Reveal')}
                </span>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAdminOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add New Admin */}
      <Dialog
        open={addAdminOpen}
        onClose={() => { if (!adminActionLoading) setAddAdminOpen(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Admin</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Creates a new admin in the live tenant database. Generate a {ADMIN_PASSWORD_LENGTH}-character password or enter your own (min 12).
          </Typography>
          <TextField
            fullWidth
            label="Admin email"
            type="email"
            sx={{ mb: 2 }}
            value={adminForm.email}
            onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Display name"
            sx={{ mb: 2 }}
            value={adminForm.name}
            onChange={(e) => setAdminForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
            <TextField
              fullWidth
              label="Admin password"
              type="text"
              helperText={`${adminForm.admin_password ? adminForm.admin_password.length : 0} chars · leave blank to auto-generate ${ADMIN_PASSWORD_LENGTH}`}
              value={adminForm.admin_password}
              onChange={(e) => setAdminForm((f) => ({ ...f, admin_password: e.target.value }))}
            />
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
              onClick={() => setAdminForm((f) => ({ ...f, admin_password: generateStrongPassword() }))}
            >
              Generate
            </Button>
          </Box>
          <TextField
            fullWidth
            label="Your Super Admin password"
            type="password"
            autoComplete="current-password"
            value={adminForm.password}
            onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddAdmin(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={adminActionLoading} onClick={() => setAddAdminOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={adminActionLoading || !adminForm.email || !adminForm.password}
            onClick={handleAddAdmin}
          >
            {adminActionLoading ? <CircularProgress size={20} /> : 'Add admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Existing Admin Password */}
      <Dialog
        open={resetAdminOpen}
        onClose={() => { if (!adminActionLoading) setResetAdminOpen(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reset Existing Admin Password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Choose the admin to reset. A new {ADMIN_PASSWORD_LENGTH}-character password is generated automatically and pushed to the tenant database.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="reset-admin-user-label">Admin user</InputLabel>
            <Select
              labelId="reset-admin-user-label"
              label="Admin user"
              value={adminForm.email}
              onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
            >
              {listTenantAdmins().map((a) => (
                <MenuItem key={a.email} value={a.email}>
                  {a.email}{a.name ? ` (${a.name})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info" sx={{ mb: 2 }}>
            Password will be auto-generated ({ADMIN_PASSWORD_LENGTH} characters) when you confirm.
          </Alert>
          <TextField
            fullWidth
            label="Your Super Admin password"
            type="password"
            autoComplete="current-password"
            value={adminForm.password}
            onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleResetAdmin(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={adminActionLoading} onClick={() => setResetAdminOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={adminActionLoading || !adminForm.email || !adminForm.password}
            onClick={handleResetAdmin}
          >
            {adminActionLoading ? <CircularProgress size={20} /> : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit admin */}
      <Dialog
        open={editAdminOpen}
        onClose={() => { if (!adminActionLoading) setEditAdminOpen(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit admin</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            sx={{ mb: 2, mt: 1 }}
            value={adminForm.new_email}
            onChange={(e) => setAdminForm((f) => ({ ...f, new_email: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Display name"
            sx={{ mb: 2 }}
            value={adminForm.name}
            onChange={(e) => setAdminForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
            <TextField
              fullWidth
              label="New password (optional)"
              type="text"
              helperText="Leave blank to keep current password"
              value={adminForm.admin_password}
              onChange={(e) => setAdminForm((f) => ({ ...f, admin_password: e.target.value }))}
            />
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
              onClick={() => setAdminForm((f) => ({ ...f, admin_password: generateStrongPassword() }))}
            >
              Generate
            </Button>
          </Box>
          <TextField
            fullWidth
            label="Your Super Admin password"
            type="password"
            autoComplete="current-password"
            value={adminForm.password}
            onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={adminActionLoading} onClick={() => setEditAdminOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={adminActionLoading || !adminForm.email || !adminForm.password}
            onClick={handleEditAdmin}
          >
            {adminActionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete admin */}
      <Dialog
        open={deleteAdminOpen}
        onClose={() => { if (!adminActionLoading) setDeleteAdminOpen(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete admin</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Remove <strong>{adminForm.email}</strong> from this tenant? The live account will be locked by rotating its password.
          </Alert>
          <TextField
            fullWidth
            label="Your Super Admin password"
            type="password"
            autoComplete="current-password"
            value={adminForm.password}
            onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={adminActionLoading} onClick={() => setDeleteAdminOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={adminActionLoading || !adminForm.email || !adminForm.password}
            onClick={handleDeleteAdmin}
          >
            {adminActionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

            <TenantDestroyReportDialog
        open={destroyReportOpen}
        report={destroyReport}
        title={destroyReport?.infrastructure?.success === false || (destroyReport?.infrastructure?.errors || []).length
          ? 'Destroy incomplete — review steps'
          : 'Destroy complete — review steps'}
        onClose={() => {
          setDestroyReportOpen(false);
          const incomplete = (destroyReport?.infrastructure?.errors || []).length > 0
            || destroyReport?.infrastructure?.success === false;
          const asyncStarted = destroyReport?.mode === 'async_destroy' || destroyReport?.status === 'destroying';
          setDestroyReport(null);
          if (incomplete || asyncStarted) {
            fetchTenant(false);
            if (asyncStarted) navigate('/tenants');
            return;
          }
          navigate('/tenants');
        }}
      />

      <TenantProvisionWizard
        tenantId={id}
        tenant={tenant}
        confirmOpen={provisionConfirmOpen}
        onCloseConfirm={() => setProvisionConfirmOpen(false)}
        progressOpen={provisionPanelOpen}
        onOpenProgress={() => setProvisionPanelOpen(true)}
        onCloseProgress={() => setProvisionPanelOpen(false)}
        onTenantRefresh={() => fetchTenant(false)}
      />
    </Box>
  );
};

export default TenantDetail;
